// AppSidebar — persistent 60px icon sidebar for app-wide navigation
import React from "react";
import { Box, IconButton, Tooltip, Avatar } from "@mui/material";
import { useHistory, useLocation } from "react-router-dom";
import type { MenuRoute } from "../routes/routes";
import { menuRoutes, adminRoutes } from "../routes/routes";
import {
  ChartPieSlice,
  MapTrifold,
  Rows,
  SealCheck,
  Screencast,
  ChatCircle,
  GearSix,
  SignOut,
} from "@phosphor-icons/react";
import { useSession, useAllowedRoutes, RoleName } from "../lib/session";
import useRbac from "../lib/rbac";
import { useAuth0 } from "../components/react-auth0-spa";
import notFoundImage from "../assets/images/not_found.png";

const SIDEBAR_WIDTH = 60;

const sidebarPalette = {
  bg: "#1c1c22",
  border: "#2c2c35",
  iconDefault: "#6e6e7a",
  iconHover: "#ededf0",
  iconActive: "#eab308",
  activeBg: "rgba(234, 179, 8, 0.1)",
  hoverBg: "rgba(255, 255, 255, 0.04)",
};

function getActiveSection(pathname: string, routes: MenuRoute[]): string | null {
  for (const route of routes) {
    if (pathname === route.path || pathname.startsWith(route.path + "/")) {
      return route.parentMenu || route.name;
    }
  }
  return null;
}

// Top-level nav sections — order matches the sidebar top-to-bottom
const NAV_SECTIONS = [
  { key: "Dashboard", icon: ChartPieSlice, defaultPath: "/cpo-dashboard" },
  { key: "Site View", icon: MapTrifold, defaultPath: "/siteView" },
  { key: "Program Delivery", icon: Rows, defaultPath: "/settings/shortRangePrograms" },
  { key: "Platform", icon: SealCheck, defaultPath: "/platform/list" },
  { key: "Cranecast", icon: Screencast, defaultPath: "/cranecastAnalytics" },
  { key: "Chat", icon: ChatCircle, defaultPath: "/chat" },
] as const;

interface AppSidebarProps {
  onOpenSidebar: () => void;
}

export default function AppSidebar({ onOpenSidebar }: AppSidebarProps) {
  const history = useHistory();
  const location = useLocation();
  const { activeProject } = useSession();
  const { logout, user } = useAuth0();

  // Permission-based filtering
  const userRoles: RoleName[] = useRbac(activeProject);
  const allowedMenuRoutes = useAllowedRoutes(activeProject.id, menuRoutes, userRoles);
  const allowedAdminRoutes = useAllowedRoutes(activeProject.id, adminRoutes, userRoles);
  const allAllowed = [...allowedMenuRoutes, ...allowedAdminRoutes];

  // Group allowed routes by parentMenu
  const sectionRoutes = new Map<string, MenuRoute[]>();
  for (const route of allAllowed) {
    const key = route.parentMenu || route.name;
    if (!sectionRoutes.has(key)) sectionRoutes.set(key, []);
    sectionRoutes.get(key)!.push(route);
  }

  // Filter NAV_SECTIONS to only show icons the user has access to
  const visibleSections = NAV_SECTIONS.filter(({ key }) => sectionRoutes.has(key));

  const activeSection = getActiveSection(location.pathname, allAllowed);
  const isSettingsActive = location.pathname.startsWith("/settings") && !activeSection;

  const hasSettingsPermission =
    userRoles.includes(RoleName.SITEADMIN) || userRoles.includes(RoleName.ADMIN);

  const handleLogout = () => {
    localStorage.removeItem("token");
    logout({ returnTo: window.location.origin });
  };

  const iconSx = (isActive: boolean) => ({
    width: 40,
    height: 40,
    borderRadius: "8px",
    color: isActive ? sidebarPalette.iconActive : sidebarPalette.iconDefault,
    bgcolor: isActive ? sidebarPalette.activeBg : "transparent",
    transition: "all 0.15s",
    "&:hover": {
      bgcolor: isActive ? sidebarPalette.activeBg : sidebarPalette.hoverBg,
      color: isActive ? sidebarPalette.iconActive : sidebarPalette.iconHover,
    },
  });

  return (
    <Box
      component="nav"
      sx={{
        width: SIDEBAR_WIDTH,
        flexShrink: 0,
        bgcolor: sidebarPalette.bg,
        borderRight: `1px solid ${sidebarPalette.border}`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        py: 1.5,
        gap: 0.5,
      }}
    >
      {/* Project avatar */}
      <Tooltip title={activeProject.name || "Switch project"} placement="right">
        <IconButton
          onClick={() => history.push("/projects")}
          sx={{ mb: 1.5, p: 0.5 }}
        >
          <Avatar
            src={activeProject.image ?? notFoundImage}
            variant="rounded"
            sx={{ width: 32, height: 32, borderRadius: "8px" }}
          />
        </IconButton>
      </Tooltip>

      {/* Nav section icons */}
      {visibleSections.map(({ key, icon: Icon, defaultPath }) => {
        const isActive = activeSection === key;
        const subRoutes = sectionRoutes.get(key) || [];
        const hasMultipleRoutes = subRoutes.length > 1;

        // Single-item section — click navigates directly
        // Multi-item section — click opens the full sidebar drawer
        return (
          <Tooltip key={key} title={key} placement="right">
            <IconButton
              onClick={() => {
                if (hasMultipleRoutes) {
                  onOpenSidebar();
                } else {
                  history.push(defaultPath);
                }
              }}
              sx={iconSx(isActive)}
            >
              <Icon size={20} />
            </IconButton>
          </Tooltip>
        );
      })}

      {/* Spacer */}
      <Box sx={{ flex: 1 }} />

      {/* Settings — only for SITEADMIN or ADMIN */}
      {hasSettingsPermission && (
        <Tooltip title="Settings" placement="right">
          <IconButton
            onClick={() => history.push("/settings")}
            sx={iconSx(isSettingsActive)}
          >
            <GearSix size={20} />
          </IconButton>
        </Tooltip>
      )}

      {/* Logout */}
      <Tooltip title={user?.email ? `${user.email} — Log out` : "Log out"} placement="right">
        <IconButton onClick={handleLogout} sx={iconSx(false)}>
          <SignOut size={20} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}
